import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const OVERLAY_Z_INDEX = 1000;

export interface MapOverlayContainerProps {
  map: google.maps.Map;
  overlayComponent: JSX.Element | null;
  anchorLatLng: google.maps.LatLng;
  id: string;
  hidden?: boolean;
}

/**
 * Reusable component for displaying an overlay on the Insights map.
 * Note: If overlayComponent has mouse events (e.g. buttons), make sure to prevent event propagation.
 * @param map The Google Maps instance to attach the overlay to.
 * @param overlayComponent The React component to render as the overlay.
 * @param anchorLatLng The latitude and longitude where the overlay should be anchored.
 * @param id A unique identifier for the overlay, used for testing and debugging.
 * @param hidden Optional flag to hide the overlay (default is false).
 * @returns A React portal that renders the overlay component in the correct position on the map.
 */
const MapOverlayContainer: React.FC<MapOverlayContainerProps> = (props: MapOverlayContainerProps) => {
  const { map, overlayComponent, anchorLatLng, id, hidden = false } = props;

  const containerElement = useRef<HTMLDivElement | null>(document.createElement('div'));

  const [overlayView] = useState(new google.maps.OverlayView());

  // Memoize the draw method to change when props change
  const drawCallback = useCallback((): void => {
    const overlayProjection = overlayView.getProjection();
    if (containerElement.current && overlayProjection && anchorLatLng) {
      // Get the pixel position of the display location (i.e. where the mouse is hovering)
      const point = overlayProjection.fromLatLngToDivPixel(anchorLatLng) as google.maps.Point;

      // Display the overlay
      containerElement.current.style.left = `${point.x}px`;
      containerElement.current.style.top = `${point.y}px`;
      containerElement.current.style.zIndex = OVERLAY_Z_INDEX.toString();
      containerElement.current.style.display = hidden ? 'none' : 'block';
    }
  }, [overlayView, anchorLatLng, hidden]);

  const onAddCallback = useCallback((): void => {
    if (containerElement.current) {
      containerElement.current.style.position = 'absolute';
      containerElement.current.setAttribute('data-test', `map__overlay__${id}`);

      const panes = overlayView.getPanes();
      if (panes) {
        panes.floatPane.appendChild(containerElement.current);
      }
    }
  }, [overlayView, id]);

  const onRemove = (): void => {
    // Remove the overlay from the DOM
    if (containerElement?.current?.parentNode) {
      containerElement.current.parentNode.removeChild(containerElement.current);
    }
    containerElement.current = null;
  };

  // Set up overlayView
  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.onAdd = onAddCallback;
    overlayView.onRemove = onRemove;
    overlayView.setMap(map);

    return () => {
      // Remove the overlay view from the map
      overlayView.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redraw the overlay when the drawCallback changes
  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.draw();
  }, [drawCallback, overlayView]);

  // render is used to keep this component in the virtual dom. The actual
  // DOM will be set in onAdd() for the google.maps.OverlayView()
  return containerElement.current ? createPortal(overlayComponent, containerElement.current) : null;
};

export default MapOverlayContainer;
